import axios from "axios";

const BACKEND_URL = window.location.origin;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
  getLoginLogs: (params) => api.get("/auth/login-logs", { params }),
};

export const adminNotificationsAPI = {
  getNotifications: () => api.get("/admin/notifications"),
};

export const usersAPI = {
  getAll: () => api.get("/users"),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getPending: () => api.get("/users/pending"),
  approveUser: (id, data) => api.post(`/users/${id}/approve`, data),
  getVendedores: (params) => api.get("/users/vendedores", { params }),
};

export const clientsAPI = {
  getAll: () => api.get("/clients"),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkDelete: (ids) => api.post("/clients/bulk-delete", { ids }),
  // Search by phone for existing client detection
  searchByPhone: (phone) => api.get("/clients/search", { params: { phone } }),
  // Extended search with last record and reassignment count
  searchExtended: (phone) => api.get("/clients/search-extended", { params: { phone } }),
  // Reassignment flow
  requestReassignment: (id, data) => api.post(`/clients/${id}/request-reassignment`, data),
  reassign: (id, data) => api.post(`/clients/${id}/reassign`, data),
  getReassignmentRequests: () => api.get("/clients/reassignment-requests"),
  getAllReassignmentRequests: (params) => api.get("/clients/reassignment-requests/all", { params }),
  getMyReassignmentRequests: () => api.get("/clients/my-reassignment-requests"),
  processReassignmentRequest: (requestId, data) => api.post(`/clients/reassignment-requests/${requestId}/action`, data),
  getOwnerHistory: (id) => api.get(`/clients/${id}/history`),
};

// ==================== CRM CLIENTS API (v4.12.0) ====================
export const crmClientsAPI = {
  // List and search
  getAll: (params) => api.get("/crm/clients", { params }),
  search: (query, limit = 10) => api.get("/crm/clients/search", { params: { q: query, limit } }),
  getById: (id) => api.get(`/crm/clients/${id}`),
  
  // CRUD
  create: (data) => api.post("/crm/clients", data),
  update: (id, data) => api.put(`/crm/clients/${id}`, data),
  
  // Migration from legacy - Fase 3
  convertFromLegacy: ({ params, clientData }) => api.post("/crm/clients/convert-from-legacy", clientData, { params }),
  
  // Relations
  getContracts: (id) => api.get(`/crm/clients/${id}/contracts`),
  getQuotations: (id) => api.get(`/crm/clients/${id}/quotations`),
  
  // Owner management
  assignOwner: (id, newOwnerId, reason) => api.post(`/crm/clients/${id}/assign-owner`, null, { 
    params: { new_owner_id: newOwnerId, reason } 
  }),
};

export const corporatesAPI = {
  getAll: () => api.get("/corporates"),
  create: (data) => api.post("/corporates", data),
  update: (id, data) => api.put(`/corporates/${id}`, data),
};

export const driversAPI = {
  getAll: () => api.get("/drivers"),
  create: (data) => api.post("/drivers", data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
};

export const vehiclesAPI = {
  getAll: () => api.get("/vehicles"),
  create: (data) => api.post("/vehicles", data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
};

export const vehicleTypesAPI = {
  getAll: (includeInactive = false) => api.get("/vehicle-types", { params: { include_inactive: includeInactive } }),
  create: (data) => api.post("/vehicle-types", data),
  update: (id, data) => api.put(`/vehicle-types/${id}`, data),
  delete: (id) => api.delete(`/vehicle-types/${id}`),
};

export const serviceTypesAPI = {
  getAll: (includeInactive = false) => api.get("/service-types", { params: { include_inactive: includeInactive } }),
  create: (data) => api.post("/service-types", data),
  update: (id, data) => api.put(`/service-types/${id}`, data),
  delete: (id) => api.delete(`/service-types/${id}`),
};

export const fixedRoutesAPI = {
  getAll: () => api.get("/fixed-routes"),
  create: (data) => api.post("/fixed-routes", data),
  update: (id, data) => api.put(`/fixed-routes/${id}`, data),
};

export const requestsAPI = {
  getAll: (params) => api.get("/requests", { params }),
  getOne: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post("/requests", data),
  update: (id, data) => api.put(`/requests/${id}`, data),
};

export const quotesAPI = {
  getAll: () => api.get("/quotes"),
  getOne: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post("/quotes", data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  delete: (id) => api.delete(`/quotes/${id}`),
  bulkDelete: (ids) => api.post("/quotes/bulk-delete", { ids }),
  convertToStandard: (id, data) => api.post(`/quotes/${id}/convert-to-standard`, data),
  generatePDF: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  createStripePaymentLink: (id, data) => api.post(`/quotes/${id}/stripe-payment-link`, data),
};

export const quoteSettingsAPI = {
  get: () => api.get("/quote-settings"),
  update: (data) => api.put("/quote-settings", data),
};

export const contractsAPI = {
  getAll: (params) => api.get("/contracts", { params }),
  getOne: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post("/contracts", data),
  availabilityPrecheck: (data) => api.post("/contracts/availability-precheck", data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  bulkDelete: (ids) => api.post("/contracts/bulk-delete", { ids }),
  updateAssignment: (id, data) => api.put(`/contracts/${id}/assignment`, data),
  updateStatus: (id, status, completionNote) => {
    let url = `/contracts/${id}/status?status=${status}`;
    if (completionNote) url += `&completion_note=${encodeURIComponent(completionNote)}`;
    return api.put(url);
  },
  getLogs: (id) => api.get(`/contracts/${id}/logs`),
  // Sales owner history (audit log for salesperson changes)
  getSalesOwnerHistory: (id) => api.get(`/contracts/${id}/sales-owner-history`),
  // PDF generation
  getPDF: (id) => api.get(`/contracts/${id}/pdf`, { responseType: 'blob' }),
  getServiceOrderPDF: (id) => api.get(`/contracts/${id}/service-order-pdf`, { responseType: 'blob' }),
  // NEW: Service orders by unit
  getServiceOrders: (id) => api.get(`/contracts/${id}/service-orders`),
  getUnitServiceOrderPDF: (id, unitId) => api.get(`/contracts/${id}/service-order-pdf/${unitId}`, { responseType: 'blob' }),
  // Admin: Reassign contract to another sales owner
  reassign: (id, data) => api.put(`/contracts/${id}/reassign`, data),
  // Multiple assignments (v2)
  getAssignments: (id) => api.get(`/contracts/${id}/assignments`),
  createAssignment: (id, data) => api.post(`/contracts/${id}/assignments`, data),
  updateAssignment2: (contractId, assignmentId, data) => api.put(`/contracts/${contractId}/assignments/${assignmentId}`, data),
  deleteAssignment: (contractId, assignmentId, postServiceReason = null) => {
    const params = postServiceReason ? `?post_service_change_reason=${encodeURIComponent(postServiceReason)}` : "";
    return api.delete(`/contracts/${contractId}/assignments/${assignmentId}${params}`);
  },
  getAssignmentAudit: (contractId, assignmentId) => api.get(`/contracts/${contractId}/assignments/${assignmentId}/audit`),
  // Income distribution per unit
  getIncomeDistribution: (id) => api.get(`/contracts/${id}/income-distribution`),
  updateIncomeDistribution: (id, data) => api.put(`/contracts/${id}/income-distribution`, data),
  // P2: Contractual client data editing
  editClientData: (id, data) => api.put(`/contracts/${id}/client-data`, data),
  getClientEditHistory: (id) => api.get(`/contracts/${id}/client-edit-history`),
};

export const availabilityAPI = {
  check: (params) => api.get("/availability/check", { params }),
  dashboard: (params) => api.get("/availability/dashboard", { params }),
  // v4.58.0
  pressureByType: (params) => api.get("/availability/pressure-by-type", { params }),
};

export const paymentsAPI = {
  getAll: (contractId) => api.get("/payments", { params: { contract_id: contractId } }),
  getContractSummary: (contractId) => api.get(`/payments/contract-summary/${contractId}`),
  create: (data) => api.post("/payments", data),
  // Soft-delete pago manual (admin only) — v4.49.0
  delete: (contractId, paymentId, data) => api.delete(`/contracts/${contractId}/payments/${paymentId}`, { data }),
  // Stripe endpoints
  createStripeCheckout: (data) => api.post("/payments/stripe/create-checkout", data),
  getStripeStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`),
  getPendingCheckout: (contractId) => api.get(`/payments/stripe/pending-checkout/${contractId}`),
  // Payment links — flexible link generation (Fase Ligas)
  getPaymentSummary: (contractId) => api.get(`/contracts/${contractId}/payment-summary`),
  listPaymentLinks: (contractId) => api.get(`/contracts/${contractId}/payment-links`),
};

export const bankAccountsAPI = {
  getAll: (params) => api.get("/bank-accounts", { params }),
  create: (data) => api.post("/bank-accounts", data),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/bank-accounts/${id}`),
};

export const cardTerminalsAPI = {
  getAll: (params) => api.get("/card-terminals", { params }),
  create: (data) => api.post("/card-terminals", data),
  update: (id, data) => api.put(`/card-terminals/${id}`, data),
  delete: (id) => api.delete(`/card-terminals/${id}`),
};

export const attachmentsAPI = {
  getAll: (contractId) => api.get(`/attachments/${contractId}`),
  upload: (contractId, type, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/attachments/${contractId}?attachment_type=${type}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  download: (id) => api.get(`/attachments/download/${id}`, { responseType: "blob" }),
  previewUrl: (id) => `${api.defaults.baseURL}/attachments/preview/${id}`,
  delete: (id) => api.delete(`/attachments/${id}`),
};

export const financeAPI = {
  getCxC: (params) => api.get("/accounts-receivable", { params }),
  getCxP: () => api.get("/accounts-payable"),
  createCxP: (data) => api.post("/accounts-payable", data),
  payCxP: (id) => api.put(`/accounts-payable/${id}/pay`),
  getLedger: (params) => api.get("/ledger", { params }),
  createLedgerEntry: (data) => api.post("/ledger", data),
};

export const alertConfigAPI = {
  get: () => api.get("/alert-config"),
  update: (data) => api.put("/alert-config", data),
  test: () => api.post("/alert-config/test"),
  getLogs: (params) => api.get("/notification-log", { params }),
};

export const salesAgentsAPI = {
  getAll: (params) => api.get("/sales-agents", { params }),
  getOne: (id) => api.get(`/sales-agents/${id}`),
  create: (data) => api.post("/sales-agents", data),
  update: (id, data) => api.put(`/sales-agents/${id}`, data),
  delete: (id) => api.delete(`/sales-agents/${id}`),
  seedFromUsers: () => api.post("/sales-agents/seed-from-users"),
  migrateExisting: () => api.post("/sales-agents/migrate-existing"),
};

export const commissionsAPI = {
  getAll: (params) => api.get("/commissions", { params }),
  pay: (id) => api.put(`/commissions/${id}/pay`),
  updatePaidDate: (id, paidDate) => api.put(`/commissions/${id}/paid-date?paid_date=${encodeURIComponent(paidDate)}`),
  // Admin migration tools (IVA fix)
  migrationHistory: () => api.get("/admin/commissions/migrate/history"),
  migrateDryRun: () => api.post("/admin/commissions/migrate?dry_run=true"),
  migrateApply: () => api.post("/admin/commissions/migrate?dry_run=false"),
  migrateCsvUrl: (type) => `${BACKEND_URL}/api/admin/commissions/migrate/report.csv?type=${type}`,
};

export const metricsAPI = {
  getDashboard: (params) => api.get("/metrics/dashboard", { params }),
  getSalesByVendor: () => api.get("/metrics/sales-by-vendor"),
  getKPIs: () => api.get("/metrics/kpis"),
  getWeeklyServices: () => api.get("/metrics/weekly-services"),
  getFinancialDashboard: (params) => api.get("/metrics/financial-dashboard", { params }),
};

export const calendarAPI = {
  getEvents: (startDate, endDate) => api.get("/calendar", { params: { start_date: startDate, end_date: endDate } }),
};

// v4.59.2: Operational lines (capa operativa normalizada)
export const operationalLinesAPI = {
  getByContract: (contractId, params = {}) => api.get(`/contracts/${contractId}/operational-lines`, { params }),
  list: (params) => api.get("/operational-lines", { params }),
};

export const logisticaAPI = {
  getDashboard: (params) => api.get("/logistica/dashboard", { params }),
};

export const auditAPI = {
  getLogs: (params) => api.get("/audit-logs", { params }),
};

export const settingsAPI = {
  get: () => api.get("/settings"),
  update: (data) => api.put("/settings", data),
};

export const adminAPI = {
  getNotifications: () => api.get("/admin/notifications"),
  purgeAllData: () => api.delete("/admin/purge-all-data"),
  purgeTestData: () => api.post("/admin/purge-test-data"),
  purgePreview: () => api.get("/admin/purge-preview"),
  checkSuperAdmin: () => api.get("/admin/check-super-admin"),
  deleteRecord: (collection, recordId, reason) => api.post("/admin/delete-record", {
    collection,
    record_id: recordId,
    reason
  }),
  getDeletionLogs: (limit = 50) => api.get(`/admin/deletion-logs?limit=${limit}`),
};

// User Manuals
export const manualsAPI = {
  list: () => api.get("/manuals"),
  download: (manualId) => api.get(`/manuals/${manualId}/download`, { responseType: 'blob' }),
  generateAll: () => api.post("/manuals/generate-all"),
};

export const seedAPI = {
  run: () => api.post("/seed"),
};

// Contract Frames (Multi-day contracts)
export const contractFramesAPI = {
  getAll: (params) => api.get("/contract-frames", { params }),
  getOne: (id) => api.get(`/contract-frames/${id}`),
  create: (data) => api.post("/contract-frames", data),
  update: (id, data) => api.put(`/contract-frames/${id}`, data),
  delete: (id) => api.delete(`/contract-frames/${id}`),
  generatePDF: (id, templateId) => api.post(
    `/contract-frames/${id}/generate-pdf${templateId ? `?template_id=${templateId}` : ''}`,
    {},
    { responseType: 'blob' }
  ),
  getPDFVersions: (id) => api.get(`/contract-frames/${id}/pdf-versions`),
  downloadPDF: (id, version) => api.get(`/contract-frames/${id}/pdf/${version}`, { responseType: 'blob' }),
};

// PDF Templates
export const pdfTemplatesAPI = {
  getAll: () => api.get("/pdf-templates"),
  getOne: (id) => api.get(`/pdf-templates/${id}`),
  create: (data) => api.post("/pdf-templates", data),
  update: (id, data) => api.put(`/pdf-templates/${id}`, data),
  delete: (id) => api.delete(`/pdf-templates/${id}`),
};

// Alerts
export const alertsAPI = {
  getAll: (params) => api.get("/alerts", { params }),
  create: (data) => api.post("/alerts", data),
  acknowledge: (id) => api.put(`/alerts/${id}/acknowledge`),
  dismiss: (id) => api.put(`/alerts/${id}/dismiss`),
  getPendingCount: () => api.get("/alerts/pending-count"),
  getDashboardWidget: () => api.get("/alerts/dashboard-widget"),
  runMonitoring: () => api.post("/alerts/run-monitoring"),
};

// Google Maps Proxy
export const mapsAPI = {
  getStatus: () => api.get("/maps/status"),
  autocomplete: (query) => api.get("/maps/autocomplete", { params: { query } }),
  geocode: (placeId, address) => api.get("/maps/geocode", { 
    params: placeId ? { place_id: placeId } : { address } 
  }),
  getDistance: (originLat, originLng, destLat, destLng) => api.get("/maps/distance", {
    params: { origin_lat: originLat, origin_lng: originLng, dest_lat: destLat, dest_lng: destLng }
  }),
  calculateRoute: (origin, destination) => api.post("/maps/calculate-route", { origin, destination }),
  // Test connectivity with a sample query
  testConnectivity: () => api.get("/maps/test-connectivity"),
};

// Pricing Engine API
export const pricingAPI = {
  // Vehicle rates CRUD
  getVehicleRates: () => api.get("/pricing/vehicle-rates"),
  createVehicleRate: (data) => api.post("/pricing/vehicle-rates", data),
  updateVehicleRate: (id, data) => api.put(`/pricing/vehicle-rates/${id}`, data),
  deleteVehicleRate: (id) => api.delete(`/pricing/vehicle-rates/${id}`),
  
  // Price suggestion
  suggestPrice: (data) => api.post("/pricing/suggest", data),
  
  // Seed default rates
  seedRates: () => api.get("/pricing/seed-rates"),
};

// Excel Export
export const exportAPI = {
  getAvailableSheets: () => api.get("/export/available-sheets"),
  exportExcel: (filters) => api.post("/export/excel", filters, { responseType: 'blob' }),
};

// Maintenance Module API
export const maintenanceAPI = {
  // Maintenance Logs (Bitácoras)
  getLogs: (params) => api.get("/maintenance/logs", { params }),
  getLog: (id) => api.get(`/maintenance/logs/${id}`),
  createLog: (data) => api.post("/maintenance/logs", data),
  updateLog: (id, data) => api.put(`/maintenance/logs/${id}`, data),
  deleteLog: (id) => api.delete(`/maintenance/logs/${id}`),
  
  // Maintenance Expenses (Gastos)
  getExpenses: (params) => api.get("/maintenance/expenses", { params }),
  createExpense: (data) => api.post("/maintenance/expenses", data),
  updateExpense: (id, data) => api.put(`/maintenance/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/maintenance/expenses/${id}`),
  
  // Unit Blocks (Bloqueos)
  getBlocks: (params) => api.get("/maintenance/blocks", { params }),
  createBlock: (data) => api.post("/maintenance/blocks", data),
  updateBlock: (id, data) => api.put(`/maintenance/blocks/${id}`, data),
  deleteBlock: (id) => api.delete(`/maintenance/blocks/${id}`),
  
  // Availability
  checkAvailability: (params) => api.get("/maintenance/units/availability", { params }),
  
  // Stats
  getStats: () => api.get("/maintenance/stats"),
};

// Expense Types API (Dynamic Catalog)
export const expenseTypesAPI = {
  getAll: () => api.get("/expense-types"),
  create: (data) => api.post("/expense-types", data),
  update: (id, data) => api.put(`/expense-types/${id}`, data),
  delete: (id) => api.delete(`/expense-types/${id}`),
};

// Unit Expenses API (Financial Control)
export const unitExpensesAPI = {
  getAll: (params) => api.get("/unit-expenses", { params }),
  create: (data) => api.post("/unit-expenses", data),
  update: (id, data) => api.put(`/unit-expenses/${id}`, data),
  delete: (id) => api.delete(`/unit-expenses/${id}`),
};

// Unit Financials API
export const unitFinancialsAPI = {
  getUnitFinancials: (unitId, params) => api.get(`/units/${unitId}/financials`, { params }),
  getGlobalFinancials: (params) => api.get("/financials/global", { params }),
  assignIncome: (unitId, data) => api.post(`/units/${unitId}/income-assignment`, data),
};
